// const webSocketURL = "ws://project-planner-36424-staging.botics.co/ws/chat/";

// class WebSocketConnection {
//   constructor() {
//     this.socket = null;
//   }

//   connect() {
//     this.socket = new WebSocket(webSocketURL);

//     this.socket.onopen = () => {
//       console.log("WebSocket connected");
//       // Perform any actions upon successful connection
//     };

//     this.socket.onmessage = (event) => {
//       const message = event.data;
//       console.log("Received message:", message);
//       // Handle incoming messages
//     };

//     this.socket.onclose = () => {
//       console.log("WebSocket disconnected");
//       // Handle disconnection
//     };
//   }

//   send(message) {
//     if (this.socket && this.socket.readyState === WebSocket.OPEN) {
//       this.socket.send(message);
//     } else {
//       console.log("WebSocket connection not established");
//     }
//   }

//   close() {
//     if (this.socket) {
//       this.socket.close();
//     }
//   }
// }

// export default WebSocketConnection;

// websocket.js

import { apiWebsocketURL } from "./constants";
const webSocketURL = apiWebsocketURL;

// const webSocketURL = "project-planner-36424.botics.co/ws/chat/"; // production
// const webSocketURL = "wss://project-planner-36424-staging.botics.co/ws/chat/"; //stagging

// ARO-7365: every AI request ends with a reply or a readable error.
// The backend gives OpenAI 120s (plus prompt building); wait a bit longer so a
// slow-but-successful reply is not reported as a timeout. Trade-off: a reply
// that is really lost still keeps the modal loading for up to 150s.
export const AI_REPLY_TIMEOUT_MS = 150000;
export const AI_NOT_CONNECTED_MESSAGE =
  "The AI assistant is not connected. Please try again in a moment.";
export const AI_CONNECTION_LOST_MESSAGE =
  "The connection to the AI assistant was lost. Please try again.";
export const AI_TIMEOUT_MESSAGE = "AI interruption, please try again later.";

// Payloads can carry whole uploaded files; only look at the start of the
// string for the endpoint instead of parsing the full JSON.
const ENDPOINT_RE = /"endpoint"\s*:\s*"([^"]+)"/;
export function sniffEndpoint(message) {
  if (typeof message !== "string") return null;
  const match = ENDPOINT_RE.exec(message.slice(0, 300));
  return match ? match[1] : null;
}

function isHeartbeat(message) {
  if (typeof message !== "string" || message.length > 100) return false;
  return /"p[io]ng"\s*:/.test(message);
}

export class WebSocketConnection {
  constructor() {
    this.socket = null;
    this.messageCallback = null;
    this.pending = new Map(); // endpoint -> timeout id
    this.reconnectInterval = 1000; // Initial reconnect interval in ms
    this.maxReconnectInterval = 16000; // Max interval limit
    this.connect(); // Initial connection
  }

  connect() {
    this.socket = new WebSocket(webSocketURL);

    this.socket.onopen = () => {
      console.log("WebSocket connected");
      this.reconnectInterval = 1000; // Reset interval on successful connection
    };

    this.socket.onmessage = (event) => {
      const message = event.data;
      if (!isHeartbeat(message)) {
        this.clearPending(sniffEndpoint(message));
      }
      if (this.messageCallback) {
        this.messageCallback(message); // Pass the received message to the callback function
      }
    };

    this.socket.onclose = () => {
      console.log("WebSocket disconnected, attempting to reconnect...");
      // Requests in flight will never be answered on the new socket.
      this.failAllPending(AI_CONNECTION_LOST_MESSAGE);
      this.reconnect();
    };

    this.socket.onerror = (error) => {
      console.error("WebSocket error:", error);
      this.socket.close(); // Close the socket to trigger reconnection
    };
  }

  reconnect() {
    setTimeout(() => {
      this.connect();
      this.reconnectInterval = Math.min(
        this.reconnectInterval * 2,
        this.maxReconnectInterval
      ); // Increase interval up to the maximum limit
    }, this.reconnectInterval);
  }

  send(message, endpoint = sniffEndpoint(message)) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(message);
      if (endpoint) {
        this.clearPending(endpoint);
        this.pending.set(
          endpoint,
          setTimeout(() => this.failPending(endpoint, AI_TIMEOUT_MESSAGE), AI_REPLY_TIMEOUT_MS)
        );
      }
    } else {
      console.log("WebSocket connection not established");
      if (endpoint) {
        // Report asynchronously (after the caller has set its loading state)
        // and to the callback registered now, never re-sending anything.
        const callback = this.messageCallback;
        setTimeout(() => {
          if (callback && callback === this.messageCallback) {
            this.reportError(endpoint, AI_NOT_CONNECTED_MESSAGE);
          }
        }, 0);
      }
    }
  }

  clearPending(endpoint) {
    if (!endpoint || !this.pending.has(endpoint)) return;
    clearTimeout(this.pending.get(endpoint));
    this.pending.delete(endpoint);
  }

  clearAllPending() {
    this.pending.forEach((timer) => clearTimeout(timer));
    this.pending.clear();
  }

  failPending(endpoint, errorMessage) {
    if (!this.pending.has(endpoint)) return;
    this.clearPending(endpoint);
    this.reportError(endpoint, errorMessage);
  }

  failAllPending(errorMessage) {
    Array.from(this.pending.keys()).forEach((endpoint) =>
      this.failPending(endpoint, errorMessage)
    );
  }

  // Same shape as a backend failure reply: every AI consumer stops loading
  // and can show message.error.message.
  reportError(endpoint, errorMessage) {
    if (!this.messageCallback) return;
    this.messageCallback(
      JSON.stringify({
        endpoint,
        message: { error: { message: errorMessage }, message: errorMessage, status: 0 },
      })
    );
  }

  close() {
    this.clearAllPending();
    if (this.socket) {
      this.socket.onclose = null; // Prevent reconnect on manual close
      this.socket.close();
    }
  }

  setMessageCallback(callback) {
    // Pending timeouts belong to the previous consumer (e.g. a closed modal);
    // never deliver them to a different one.
    if (callback !== this.messageCallback) {
      this.clearAllPending();
    }
    this.messageCallback = callback;
    this.errorCallback = callback;
  }

  isConnected() {
    return this.socket && this.socket.readyState === WebSocket.OPEN;
  }
}

export const websocketConnection = new WebSocketConnection();
